import { asset, component, defineTakeoverManifest } from '../../manifest-types';

import { menuSectionIds, pdpItemIds, shpIds } from './targets';

const PRIMARY_BUTTON_GRADIENT_START = '#c5381e';
const PRIMARY_BUTTON_GRADIENT_END = '#ef8d47';
const PRIMARY_BUTTON_GRADIENT = {
  type: 'linear' as const,
  colors: [PRIMARY_BUTTON_GRADIENT_START, PRIMARY_BUTTON_GRADIENT_END] as const,
  locations: [0, 1] as const,
  start: { x: 0, y: 0.5 },
  end: { x: 1, y: 0.5 },
};
const PDP_HEADER_HEIGHT = 64;
const PDP_HOME_BACKGROUND_IMAGE_ASPECT_RATIO = 375 / (PDP_HEADER_HEIGHT + 194);
const PDP_CUSTOMIZATION_BACKGROUND_IMAGE_ASPECT_RATIO = 375 / 68;
const PDP_SUMMARY_BACKGROUND_IMAGE_ASPECT_RATIO = 376 / (PDP_HEADER_HEIGHT + 150);

const SECTION_TILE_IMAGE_STYLE_CONFIG = {
  titleMaxWidth: 125,
  sectionTileImageStyle: {
    width: 75,
    height: 100,
  },
};

const MENU_SECTION_BACKGROUND_CONFIG = {
  headerBackgroundImage: asset('menuSectionBackgroundHeaderImage'),
  headerBackgroundImageLayout: 'cover-tabs' as const,
};

const MENU_SECTION_CONFIG = {
  ...SECTION_TILE_IMAGE_STYLE_CONFIG,
  ...MENU_SECTION_BACKGROUND_CONFIG,
  itemBackgroundColor: '#f8f1e7',
  sectionTileBackgroundImage: asset('menuSectionBackground'),
  sectionTileImage: asset('menuSectionTile'),
  fontColor: '#502314',
};

const KIDS_MEAL_SECTION_CONFIG = {
  ...SECTION_TILE_IMAGE_STYLE_CONFIG,
  ...MENU_SECTION_BACKGROUND_CONFIG,
  itemBackgroundColor: '#f8f1e7',
  sectionTileBackgroundImage: asset('menuSectionKidsMealsBackground'),
  sectionTileImage: asset('menuSectionKidsMealsTile'),
  fontColor: '#ffffff',
};

const PDP_ITEM_CONFIG_BASE = {
  sectionFontColor: '#101820',
  sectionBackgroundColor: '#f8f1e7',
  headerBackgroundColor: '#101820',
  headerFontColor: '#ffffff',
};

const MEAL_ITEM_CONFIG = {
  ...PDP_ITEM_CONFIG_BASE,
  cartItemHeader: {
    backgroundImage: asset('mealCartItemBackground'),
    backgroundImageFit: 'stretch' as const,
    layout: {
      height: 56,
      paddingHorizontal: 0,
      paddingVertical: 0,
      gap: 0,
    },
    image: {
      source: asset('mealCartItem'),
      width: 92,
      height: 56,
      marginLeft: 18,
      marginRight: -12,
      fit: 'contain' as const,
    },
    title: {
      color: '#502314',
      maxWidth: 320,
    },

    favoriteAction: {
      top: 8,
      right: 4,
    },
  },
};

const KIDS_ITEM_CONFIG = {
  ...PDP_ITEM_CONFIG_BASE,
  cartItemHeader: {
    backgroundImage: asset('kidsCartItemBackground'),
    backgroundImageFit: 'cover' as const,
    layout: {
      height: 56,
      paddingHorizontal: 0,
      paddingVertical: 0,
      gap: 16,
    },
    image: {
      source: asset('kidsCartItem'),
      width: 54,
      height: 56,
      fit: 'contain' as const,
    },
    title: {
      color: '#ffffff',
      fontSize: 14,
      lineHeight: 16,
      maxWidth: 230,
    },

    favoriteAction: {
      top: 8,
      right: 4,
    },
  },
};

export const manifest = defineTakeoverManifest({
  id: 'dragonball',
  ldName: 'dragonball',
  status: 'draft',
  regions: ['US', 'CA'],
  displayName: 'Dragonball',
  assets: {
    menuSectionBackground: {
      path: 'assets/menu-section-background.png',
      role: 'menu-section-background',
      status: 'final',
    },
    menuSectionBackgroundHeaderImage: {
      path: 'assets/menu-section-background-header-image.png',
      role: 'menu-section-background-header-image',
      status: 'final',
    },
    menuSectionTile: {
      path: 'assets/menu-section-tile.png',
      role: 'menu-section-tile',
      status: 'final',
    },
    menuSectionKidsMealsTile: {
      path: 'assets/menu-section-kids-meals-tile.png',
      role: 'menu-section-kids-meals-tile',
      status: 'final',
    },
    menuSectionKidsMealsBackground: {
      path: 'assets/menu-section-kids-meal-background.png',
      role: 'menu-section-kids-meals-background',
      status: 'final',
    },
    pdpOverviewTop: {
      path: 'assets/pdp-overview-top.png',
      role: 'pdp.overviewTop',
      status: 'final',
    },
    pdpHeaderFlair: {
      path: 'assets/pdp-header-flair.png',
      role: 'pdp.headerFlair',
      status: 'final',
    },
    pdpHeaderFlairCustomization: {
      path: 'assets/pdp-header-flair-customization.png',
      role: 'pdp.headerFlair.customization',
      status: 'final',
    },
    pdpSummaryBackground: {
      path: 'assets/pdp-summary-page-bg.png',
      role: 'pdp.summaryBackground',
      status: 'final',
    },
    mealCartItemBackground: {
      path: 'assets/meal-cart-item-background.png',
      role: 'cart.meal.background',
      status: 'final',
    },
    mealCartItem: {
      path: 'assets/meal-cart-item.png',
      role: 'cart.meal.image',
      status: 'final',
    },
    kidsCartItemBackground: {
      path: 'assets/kids-cart-item-background.png',
      role: 'cart.kids.background',
      status: 'final',
    },
    kidsCartItem: {
      path: 'assets/kids-cart-item.png',
      role: 'cart.kids.image',
      status: 'final',
    },
    checkout: {
      path: 'assets/checkout.png',
      role: 'paymentProcessing.image',
      status: 'final',
    },
    orderConfirmHeader: {
      path: 'assets/order-confirm-details-header.png',
      role: 'orderConfirm.header',
      status: 'final',
    },
    orderConfirmHeaderOverlay: {
      path: 'assets/order-confirm-details-header-overlay.png',
      role: 'orderConfirm.overlay',
      status: 'final',
    },
  },
  config: {
    primaryActionButton: {
      backgroundColor: PRIMARY_BUTTON_GRADIENT_START,
      gradient: PRIMARY_BUTTON_GRADIENT,
      borderWidth: 0,
    },
    homePage: {
      targetSimpleHomePageIds: shpIds,
      containerBackgroundColor: '#f8f1e7',
      primaryButton: {
        backgroundColor: PRIMARY_BUTTON_GRADIENT_START,
        gradient: PRIMARY_BUTTON_GRADIENT,
        borderWidth: 0,
        _text: { color: '#ffffff' },
      },
      deliveryButton: {
        backgroundColor: '#f8f1e7',
        borderColor: '#502314',
        borderWidth: 1,
        _text: { color: '#502314' },
      },
      showReplayButton: false,
    },
    menu: {
      targetSectionGroups: [
        { ids: menuSectionIds.meals, config: MENU_SECTION_CONFIG },
        { ids: menuSectionIds.kids, config: KIDS_MEAL_SECTION_CONFIG },
      ],
    },
    pdp: {
      targetMenuItemGroups: [
        { ids: pdpItemIds.meal, config: MEAL_ITEM_CONFIG },
        { ids: pdpItemIds.kids, config: KIDS_ITEM_CONFIG },
      ],
      overviewTopImage: asset('pdpOverviewTop'),
      overviewTopImageStyle: {
        aspectRatio: PDP_HOME_BACKGROUND_IMAGE_ASPECT_RATIO,
        backgroundColor: '#f8f1e7',
        top: -PDP_HEADER_HEIGHT,
      },
      summaryPageBackgroundImage: asset('pdpSummaryBackground'),
      summaryPageBackgroundImageStyle: {
        aspectRatio: PDP_SUMMARY_BACKGROUND_IMAGE_ASPECT_RATIO,
        marginBottom: -PDP_HEADER_HEIGHT,
        marginTop: -PDP_HEADER_HEIGHT,
      },
      summaryPageTextColor: '#101820',
      backgroundColor: '#f8f1e7',
      priceAndCaloriesFontColor: '#101820',
      headerBackgroundColor: '#502314',
      headerCustomizationBackgroundColor: '#f8f1e7',
      headerReviewBackgroundColor: 'transparent',
      headerFontColor: '#502314',
      HeaderFlair: component('HeaderFlair', {
        aspectRatio: PDP_HOME_BACKGROUND_IMAGE_ASPECT_RATIO,
        backgroundColor: '#f8f1e7',
        customizationAspectRatio: PDP_CUSTOMIZATION_BACKGROUND_IMAGE_ASPECT_RATIO,
        customizationBackgroundColor: '#f8f1e7',
        customizationSource: asset('pdpHeaderFlairCustomization'),
        reviewAspectRatio: PDP_SUMMARY_BACKGROUND_IMAGE_ASPECT_RATIO,
        reviewBackgroundColor: 'transparent',
        reviewSource: asset('pdpSummaryBackground'),
        source: asset('pdpHeaderFlair'),
      }),
    },
    cart: {
      paymentProcessingImage: asset('checkout'),
      paymentProcessingBackgroundColor: '#f8f1e7',
    },
    orderConfirmDetails: {
      headerImage: asset('orderConfirmHeader'),
      ConfirmHeaderOverlay: component('ConfirmHeaderOverlay', {
        source: asset('orderConfirmHeaderOverlay'),
      }),
      headerFontColor: '#502314',
    },
  },
  deletion: {
    notes:
      'To retire dragonball: remove safe-flag-defaults.ts and LD targeting entries, delete this package, regenerate the registry, and run takeover:validate --all.',
  },
});
